import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { onSnapshot } from 'firebase/firestore';
import { auth } from '../firebase.config';
import { useAgency } from '../AgencyContext';
import { Savings, CheckCircle, Star, Task, CalendarToday, Lock, TrendingUp, Assessment, InsertPhoto, VideoCameraBack } from '@mui/icons-material';
import './UserLeaderboard.css';

const UserLeaderboard = () => {
  const [user, loading] = useAuthState(auth);
  const { getAgencyCollection, getAgencyDoc } = useAgency();
  const [userStats, setUserStats] = useState(null);
  const [pricing, setPricing] = useState({});
  const [monthlyData, setMonthlyData] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const [availableMonths, setAvailableMonths] = useState([]);

  // Load pricing data
  useEffect(() => {
    if (!getAgencyDoc || !user) return;
    
    const loadPricing = async () => {
      try {
        const pricingDoc = getAgencyDoc('settings', 'user_pricing');
        if (!pricingDoc) return;
        
        const { getDoc } = await import('firebase/firestore');
        const docSnap = await getDoc(pricingDoc);
        if (docSnap.exists()) {
          setPricing(docSnap.data());
        }
      } catch (error) {
        console.error('Error loading pricing:', error);
      }
    };

    loadPricing();
  }, [getAgencyDoc, user]);

  // Load user statistics
  useEffect(() => {
    if (!getAgencyCollection || !user) return;

    const tasktrackingCollection = getAgencyCollection('tasktracking');
    if (!tasktrackingCollection) return;

    const unsubscribe = onSnapshot(tasktrackingCollection, (snapshot) => {
      const currentUserEmail = user.email;
      const userMonthlyStats = {};
      const months = new Set();

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (!data.createdAt) return;

        const createdDate = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
        const monthKey = `${createdDate.getFullYear()}-${(createdDate.getMonth() + 1).toString().padStart(2, '0')}`;
        months.add(monthKey);

        // Check if current user worked on this task
        const isL1User = data.L1username === currentUserEmail;
        const isL2User = data.L2username === currentUserEmail;
        const isQAUser = data.QAusername === currentUserEmail;

        if (isL1User || isL2User || isQAUser) {
          if (!userMonthlyStats[monthKey]) {
            userMonthlyStats[monthKey] = {
              role: isL1User ? 'L1' : isL2User ? 'L2' : 'QA',
              imagesValidated: 0,
              imagesRejected: 0,
              videosValidated: 0,
              videosRejected: 0,
              totalTasks: 0
            };
          }

          userMonthlyStats[monthKey].totalTasks++;

          if (isL1User) {
            userMonthlyStats[monthKey].imagesValidated += data.L1acceptedimages || 0;
            userMonthlyStats[monthKey].imagesRejected += data.L1rejectedimages || 0;
          }

          if (isL2User) {
            if (data.isVideo) {
              userMonthlyStats[monthKey].videosValidated += data.L2acceptedvideos || 0;
              userMonthlyStats[monthKey].videosRejected += data.L2rejectedvideos || 0;
            } else {
              userMonthlyStats[monthKey].imagesValidated += data.L2acceptedimages || 0;
              userMonthlyStats[monthKey].imagesRejected += data.L2rejectedimages || 0;
            }
          }

          if (isQAUser) {
            // QA statistics from L1 phase (always images)
            userMonthlyStats[monthKey].imagesValidated += data.QAacceptedimagesL1 || 0;
            userMonthlyStats[monthKey].imagesRejected += data.QArejectedimagesL1 || 0;

            // QA statistics from L2 phase
            if (data.hasL2Process) {
              if (data.isVideo) {
                userMonthlyStats[monthKey].videosValidated += data.QAacceptedvideosL2 || 0;
                userMonthlyStats[monthKey].videosRejected += data.QArejectedvideosL2 || 0;
              } else {
                userMonthlyStats[monthKey].imagesValidated += data.QAacceptedimagesL2 || 0;
                userMonthlyStats[monthKey].imagesRejected += data.QArejectedimagesL2 || 0;
              }
            }
          }
        }
      });

      setMonthlyData(userMonthlyStats);
      setUserStats(userMonthlyStats[selectedMonth] || null);
      setAvailableMonths(Array.from(months).sort().reverse());
    });

    return () => unsubscribe();
  }, [getAgencyCollection, user, selectedMonth]);

  // Update stats when month changes
  useEffect(() => {
    setUserStats(monthlyData[selectedMonth] || null);
  }, [selectedMonth, monthlyData]);

  const calculateEarnings = (stats) => {
    if (!stats || !user) return { total: 0, breakdown: {} };

    const userPricing = pricing[user.email] || {
      imageValidated: 0,
      imageRejected: 0,
      videoValidated: 0,
      videoRejected: 0
    };

    const breakdown = {
      imagesValidated: (stats.imagesValidated || 0) * userPricing.imageValidated,
      imagesRejected: (stats.imagesRejected || 0) * userPricing.imageRejected,
      videosValidated: (stats.videosValidated || 0) * userPricing.videoValidated,
      videosRejected: (stats.videosRejected || 0) * userPricing.videoRejected
    };

    const total = breakdown.imagesValidated + breakdown.imagesRejected + breakdown.videosValidated + breakdown.videosRejected;

    return { total, breakdown };
  };

  const getMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'l1': return 'linear-gradient(135deg, #FFD700, #FFA500)'; // Gold gradient
      case 'l2': return 'linear-gradient(135deg, #9C27B0, #E91E63)'; // Purple-Pink gradient
      case 'qa': return 'linear-gradient(135deg, #4CAF50, #8BC34A)'; // Green gradient
      default: return 'linear-gradient(135deg, #666666, #999999)';
    }
  };

  const getTotalWorkDone = (stats) => {
    if (!stats) return 0;
    return stats.imagesValidated + stats.imagesRejected + stats.videosValidated + stats.videosRejected;
  };

  const getSuccessRate = (stats) => {
    if (!stats) return 0;
    const total = getTotalWorkDone(stats);
    if (total === 0) return 0;
    const successful = stats.imagesValidated + stats.videosValidated;
    return Math.round((successful / total) * 100);
  };

  if (loading) {
    return (
      <div className="earnings-dashboard">
        <div className="earnings-dashboard__loading">
          <div className="earnings-dashboard__loading-spinner"></div>
          <p>Loading your earnings...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="earnings-dashboard">
        <div className="earnings-dashboard__error">
          <Lock className="earnings-dashboard__error-icon" />
          <h3>Access Required</h3>
          <p>Please log in to view your earnings dashboard</p>
        </div>
      </div>
    );
  }

  const earnings = userStats ? calculateEarnings(userStats) : { total: 0, breakdown: {} };
  const totalWork = getTotalWorkDone(userStats);
  const successRate = getSuccessRate(userStats);

  return (
    <div className="earnings-dashboard">
      {/* Animated Background */}
      <div className="earnings-dashboard__bg-animation">
        <div className="earnings-dashboard__bg-circle earnings-dashboard__bg-circle--1"></div>
        <div className="earnings-dashboard__bg-circle earnings-dashboard__bg-circle--2"></div>
        <div className="earnings-dashboard__bg-circle earnings-dashboard__bg-circle--3"></div>
      </div>

      {/* Header */}
      <div className="earnings-dashboard__header">
        <div className="earnings-dashboard__user-section">
          {userStats && (
            <div 
              className="earnings-dashboard__role-badge"
              style={{ background: getRoleColor(userStats.role) }}
            >
              <Star className="earnings-dashboard__role-icon" />
              {userStats.role} Worker
            </div>
          )}
        </div>
        
        <div className="earnings-dashboard__controls">
          <div className="earnings-dashboard__month-selector">
            <label><CalendarToday className="earnings-dashboard__calendar-icon" /> Select Month:</label>
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="earnings-dashboard__month-select"
            >
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {getMonthLabel(month)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="earnings-dashboard__content">
        {!userStats ? (
          <div className="earnings-dashboard__empty">
            <div className="earnings-dashboard__empty-animation">
              <Savings className="earnings-dashboard__empty-coin" />
            </div>
            <h2>No Work This Month</h2>
            <p>Start working on tasks to see your earnings appear here!</p>
            <div className="earnings-dashboard__empty-tip">
              <TrendingUp className="earnings-dashboard__tip-icon" /> <strong>Tip:</strong> Complete more tasks to increase your monthly income
            </div>
          </div>
        ) : (
          <div className="earnings-dashboard__stats">
            {/* Main Earnings Card */}
            <div className="earnings-dashboard__main-card">
              <div className="earnings-dashboard__earnings-header">
                <h2><Savings /> Your Earnings</h2>
                <div className="earnings-dashboard__month-badge">
                  {getMonthLabel(selectedMonth)}
                </div>
              </div>
              
              <div className="earnings-dashboard__main-amount">
                <div className="earnings-dashboard__currency">₱</div>
                <div className="earnings-dashboard__amount">
                  {earnings.total.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>

            

              {earnings.total > 0 && (
                <div className="earnings-dashboard__breakdown">
                  {earnings.breakdown.imagesValidated > 0 && (
                    <div className="earnings-dashboard__breakdown-item">
                      <InsertPhoto className="earnings-dashboard__breakdown-icon" />
                      <span>Images Approved: ₱{earnings.breakdown.imagesValidated.toFixed(2)}</span>
                    </div>
                  )}
                  {earnings.breakdown.videosValidated > 0 && (
                    <div className="earnings-dashboard__breakdown-item">
                      <InsertPhoto className="earnings-dashboard__breakdown-icon" />
                      <span>Videos Approved: ₱{earnings.breakdown.videosValidated.toFixed(2)}</span>
                    </div>
                  )}
                  {earnings.breakdown.imagesRejected > 0 && (
                    <div className="earnings-dashboard__breakdown-item">
                      <VideoCameraBack className="earnings-dashboard__breakdown-icon" />
                      <span>Images Reviewed: ₱{earnings.breakdown.imagesRejected.toFixed(2)}</span>
                    </div>
                  )}
                  {earnings.breakdown.videosRejected > 0 && (
                    <div className="earnings-dashboard__breakdown-item">
                      <VideoCameraBack className="earnings-dashboard__breakdown-icon" />
                      <span>Videos Reviewed: ₱{earnings.breakdown.videosRejected.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Stats Grid */}
            <div className="earnings-dashboard__stats-grid">
              {/* Work Completed */}
              <div className="earnings-dashboard__stat-card earnings-dashboard__stat-card--work">
                <div className="earnings-dashboard__stat-header">
                  <Task className="earnings-dashboard__stat-icon" />
                  <h3>Work Completed</h3>
                </div>
                <div className="earnings-dashboard__stat-number">{totalWork}</div>
                <div className="earnings-dashboard__stat-label">
                  {totalWork === 1 ? 'item processed' : 'items processed'}
                </div>
                <div className="earnings-dashboard__stat-progress">
                  <div className="earnings-dashboard__progress-bar">
                    <div 
                      className="earnings-dashboard__progress-fill earnings-dashboard__progress-fill--work" 
                      style={{ width: `${Math.min((totalWork / 100) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Success Rate */}
              <div className="earnings-dashboard__stat-card earnings-dashboard__stat-card--success">
                <div className="earnings-dashboard__stat-header">
                  <Star className="earnings-dashboard__stat-icon" />
                  <h3>Quality Score</h3>
                </div>
                <div className="earnings-dashboard__stat-number">{successRate}%</div>
                <div className="earnings-dashboard__stat-label">
                  {successRate >= 80 ? 'Excellent work!' : successRate >= 60 ? 'Good progress!' : 'Keep improving!'}
                </div>
                <div className="earnings-dashboard__stat-progress">
                  <div className="earnings-dashboard__progress-bar">
                    <div 
                      className="earnings-dashboard__progress-fill earnings-dashboard__progress-fill--success" 
                      style={{ width: `${successRate}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Total Tasks */}
              <div className="earnings-dashboard__stat-card earnings-dashboard__stat-card--tasks">
                <div className="earnings-dashboard__stat-header">
                  <CheckCircle className="earnings-dashboard__stat-icon" />
                  <h3>Tasks Worked</h3>
                </div>
                <div className="earnings-dashboard__stat-number">{userStats.totalTasks}</div>
                <div className="earnings-dashboard__stat-label">
                  {userStats.totalTasks === 1 ? 'task completed' : 'tasks completed'}
                </div>
                <div className="earnings-dashboard__stat-progress">
                  <div className="earnings-dashboard__progress-bar">
                    <div 
                      className="earnings-dashboard__progress-fill earnings-dashboard__progress-fill--tasks" 
                      style={{ width: `${Math.min((userStats.totalTasks / 20) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Details */}
            {(userStats.imagesValidated > 0 || userStats.videosValidated > 0) && (
              <div className="earnings-dashboard__performance">
                <h3><Assessment /> Performance Breakdown</h3>
                <div className="earnings-dashboard__performance-grid">
                  {(userStats.imagesValidated > 0 || userStats.imagesRejected > 0) && (
                    <div className="earnings-dashboard__performance-item">
                      <div className="earnings-dashboard__performance-header">
                        <Assessment className="earnings-dashboard__performance-icon" />
                        <span>Images</span>
                      </div>
                      <div className="earnings-dashboard__performance-stats">
                        <div className="earnings-dashboard__perf-stat earnings-dashboard__perf-stat--approved">
                          <div className="earnings-dashboard__perf-number">{userStats.imagesValidated}</div>
                          <div className="earnings-dashboard__perf-label">Approved</div>
                        </div>
                        {userStats.imagesRejected > 0 && (
                          <div className="earnings-dashboard__perf-stat earnings-dashboard__perf-stat--reviewed">
                            <div className="earnings-dashboard__perf-number">{userStats.imagesRejected}</div>
                            <div className="earnings-dashboard__perf-label">Reviewed</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(userStats.videosValidated > 0 || userStats.videosRejected > 0) && (
                    <div className="earnings-dashboard__performance-item">
                      <div className="earnings-dashboard__performance-header">
                        <Assessment className="earnings-dashboard__performance-icon" />
                        <span>Videos</span>
                      </div>
                      <div className="earnings-dashboard__performance-stats">
                        <div className="earnings-dashboard__perf-stat earnings-dashboard__perf-stat--approved">
                          <div className="earnings-dashboard__perf-number">{userStats.videosValidated}</div>
                          <div className="earnings-dashboard__perf-label">Approved</div>
                        </div>
                        {userStats.videosRejected > 0 && (
                          <div className="earnings-dashboard__perf-stat earnings-dashboard__perf-stat--reviewed">
                            <div className="earnings-dashboard__perf-number">{userStats.videosRejected}</div>
                            <div className="earnings-dashboard__perf-label">Reviewed</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserLeaderboard;